export function formatDateToDDMonthYYYY(date: string | Date): string {
  /**
   * Formats a date to the "DD Month YYYY" format according to the German locale.
   * @param {string | Date} date - The date to be formatted.
   * @returns {string} - The formatted date string.
   */
  if (!date) return ""
  return new Intl.DateTimeFormat("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(date))
}

export const formatToDateSpanShort = (
  starts: string | Date,
  ends: string | Date | null
): string =>
  /**
   * Formats a short date span between a start and end date.
   * @param {string | Date} starts - The start date.
   * @param {string | Date | null} ends - The end date.
   * @returns {string} - The formatted short date span.
   */
  _formatToDateSpan(starts, ends, {
    fullFormat: {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    },
    dayOnlyFormat: { weekday: "short" },
    noYearFormat: { day: "2-digit", month: "2-digit" },
  })

export const formatToDateSpanLong = (
  starts: string | Date,
  ends: string | Date | null
): string =>
  /**
   * Formats a long date span between a start and end date.
   * @param {string | Date} starts - The start date.
   * @param {string | Date | null} ends - The end date.
   * @returns {string} - The formatted long date span.
   */
  _formatToDateSpan(starts, ends, {
    fullFormat: {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    },
    dayOnlyFormat: { weekday: "short", day: "numeric" },
    noYearFormat: { weekday: "short", day: "numeric", month: "short" },
  })

function _formatToDateSpan(
  starts: string | Date,
  ends: string | Date | null,
  formats: Record<string, Intl.DateTimeFormatOptions>
): string {
  /**
   * Formats a date span between a start and end date using specified formats.
   * @param {string | Date} starts - The start date.
   * @param {string | Date | null} ends - The end date.
   * @param {Record<string, Intl.DateTimeFormatOptions>} formats - The date formats to be applied.
   * @returns {string} - The formatted date span.
   */
  const toLocalDate = (date: string | Date): Date => {
    if (!date) return null
    const utcDate = new Date(date)
    return new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate()
    )
  }
  const startsDate = toLocalDate(starts)
  const endsDate = ends ? toLocalDate(ends) : null

  if (!startsDate) return ""

  const formatDate = (
    date: Date,
    options: Intl.DateTimeFormatOptions
  ): string => new Intl.DateTimeFormat("de-DE", options).format(date)

  if (!endsDate || startsDate.toDateString() === endsDate.toDateString()) {
    return formatDate(startsDate, formats.fullFormat)
  }

  const formatStartAndEnd = (
    startDateFormat: Intl.DateTimeFormatOptions
  ): string =>
    `${formatDate(startsDate, startDateFormat)} - ${formatDate(
      endsDate,
      formats.fullFormat
    )}`

  const isSameYear = startsDate.getFullYear() === endsDate.getFullYear()
  if (
    startsDate.getMonth() === endsDate.getMonth() &&
    startsDate.getDate() === endsDate.getDate() &&
    isSameYear
  ) {
    return formatDate(startsDate, formats.fullFormat)
  }
  if (startsDate.getMonth() === endsDate.getMonth() && isSameYear) {
    return formatStartAndEnd(formats.dayOnlyFormat)
  }
  if (isSameYear) {
    return formatStartAndEnd(formats.noYearFormat)
  }

  return formatStartAndEnd(formats.fullFormat)
}

function getDiff(start: string | Date, end: string | Date): number {
  /**
   * Calculates the absolute difference in milliseconds between two dates.
   * @param {string | Date} start - The start date.
   * @param {string | Date} end - The end date.
   * @returns {number} - The difference in milliseconds.
   */
  return Math.abs(new Date(end).getTime() - new Date(start).getTime())
}

function add(date: string | Date, time: number): Date {
  /**
   * Adds a specified amount of time (in milliseconds) to a date.
   * @param {string | Date} date - The date to be adjusted.
   * @param {number} time - The amount of time to add in milliseconds.
   * @returns {Date} - The new date with the added time.
   */
  return new Date(new Date(date).getTime() + time)
}

function getDateString(date: string | Date): string {
  /**
   * Converts a date to a "YYYY-MM-DD" string.
   * @param {string | Date} date - The date to be formatted.
   * @returns {string} - The formatted date string.
   */
  return new Date(date).toISOString().split("T")[0]
}

/**
 * Returns a string representation of the time in the format "HH:MM:SS"
 * @param date
 */
function getTimeString(date: Date): string {
  return date.toISOString().split("T")[1].split(".")[0]
}

/**
 * Converts a time string in the format "HH:MM" to minutes
 * @param timeString
 */
function toMinutes(timeString: string): number {
  /**
   * Converts a time string ("HH:MM") to the total number of minutes.
   * @param {string} timeString - The time string to be converted.
   * @returns {number} - The total number of minutes.
   */
  const [hours, minutes] = timeString.split(":").map(Number)
  return hours * 60 + minutes
}

function isBeforeStr(a: string, b: string): boolean {
  /**
   * Checks if one date string is before another.
   * @param {string} a - The first date string.
   * @param {string} b - The second date string.
   * @returns {boolean} - True if a is before b, otherwise false.
   */
  return new Date(a) < new Date(b)
}

export const DateUtil = {
  formatToDateSpanShort,
  formatToDateSpanLong,
  getDiff,
  add,
  getDateString,
  getTimeString,
  toMinutes,
  isBeforeStr,
}
